import { AuthService } from '@/services/auth.service'
import UserService from '@/services/user.service'
import { UserStorageService, TokenService } from '@/services/storage.service'
import router from '@/router'

const state = {
    me: null,
    authenticating: false,
    accessToken: TokenService.getToken(),
    authenticationErrorCode: 0,
    authenticationError: '',
    refreshTokenPromise: null, // Holds the promise of the refresh token
    requestError: false
}

const getters = {
    loggedIn: state => {
        return state.accessToken ? true : false
    },

    authenticationErrorCode: state => {
        return state.authenticationErrorCode
    },

    authenticationError: state => {
        return state.authenticationError
    },

    authenticating: state => {
        return state.authenticating
    }
}

const actions = {
    async me({ commit, dispatch }, args) {
        if (state.me && state.me.permissions) return null
        try {
            let user
            if (args && args.offline) user = await UserStorageService.get()
            else {
                user = (await UserService.me()).data
                await UserStorageService.save(
                    user.id,
                    user.name,
                    user.username,
                    user.avatar
                )
            }
            if (!user || !user.username) throw new Error('Could not fetch user')
            commit('readMeSuccess', user)
            return null
        } catch (e) {
            dispatch('lock')
            if (e && e.response) return e.response.status
            else return 500
        }
    },

    async login({ commit }, { username, password }) {
        commit('loginRequest')

        try {
            const token = await AuthService.login(username, password)
            commit('loginSuccess', token)

            // Redirect the user to the page he first tried to visit or to the home view
            router.push(router.history.current.query.redirect || '/')

            return null
        } catch (e) {
            if (e && e.response) return e.response.status
            else return 500
        }
    },

    logout({ commit }) {
        AuthService.logout()
        commit('logoutSuccess')
        router.push('/login')
    },

    lock() {
        AuthService.lock()
        if (router.currentRoute.path != '/lock')
            router.push({
                path: '/lock',
                query: { redirect: router.currentRoute.fullPath }
            })
    },

    generateToken: async ({ commit }, { token }) => {
        try {
            const response = await AuthService.accessToken(token)
            commit('readMeSuccess', response)
            return null
        } catch (e) {
            commit('readAccessTokenFail')
            if (e && e.response) return e.response.status
            else return 500
        }
    },

    refreshToken({ commit, state }) {
        // If this is the first time the refreshToken has been called, make a request
        // otherwise return the same promise to the caller
        if (!state.refreshTokenPromise) {
            const p = AuthService.refreshToken()
            commit('refreshTokenPromise', p)

            // Wait for the AuthService.refreshToken() to resolve. On success set the token and clear promise
            // Clear the promise on error as well.
            p.then(
                response => {
                    commit('refreshTokenPromise', null)
                    commit('loginSuccess', response)
                },
                error => {
                    commit('refreshTokenPromise', null)
                }
            )
        }

        return state.refreshTokenPromise
    }
}

const mutations = {
    readMeSuccess(state, me) {
        state.me = me
    },

    loginRequest(state) {
        state.authenticating = true
        state.authenticationError = ''
        state.authenticationErrorCode = 0
    },

    loginSuccess(state, accessToken) {
        state.accessToken = accessToken
        state.authenticating = false
    },

    loginError(state, { errorCode, errorMessage }) {
        state.authenticating = false
        state.authenticationErrorCode = errorCode
        state.authenticationError = errorMessage
    },

    logoutSuccess(state) {
        state.me = null
        state.accessToken = ''
    },

    async readAccessTokenFail(state) {
        await AuthService.logout()
        state.requestError = true
    },

    refreshTokenPromise(state, promise) {
        state.refreshTokenPromise = promise
    }
}

const auth = {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}

export default auth
export { auth }
